<template>
    <div class="flex flex-col gap-10">
        <CPreLoader v-if="isInitialLoading" :loaded="!isInitialLoading" />

        <header class="flex flex-col gap-6 md:flex-row">
            <div class="flex flex-1 flex-col">
                <CHeading class="!mb-2" tag="h1" variant="1">Eduvalue - Mapujeme inovácie vo vzdelávaní</CHeading>

                <CHeading class="!mb-5 font-medium italic text-gray-500 dark:text-txt-body" tag="h2" variant="3">
                    Vzdelávanie je kľúčom k budúcnosti Slovenska.
                </CHeading>

                <p class="!mb-0 leading-7">
                    Na Slovensku sa realizujú stovky inovatívnych projektov vo vzdelávaní. Eduvalue ich mapuje, prepája
                    a rozvíja na jednom online mieste. Tento prehľad slúži ako cenný zdroj aktuálnych informácií pre
                    organizácie, školy, samosprávy, firmy, donorov, rodičov, študentov a inovátorov. Mapovanie zároveň
                    inšpiruje vznik nových projektov tým, že identifikuje a vypĺňa existujúce medzery vo vzdelávaní.
                    Zároveň slúži ako pomôcka pre prepájanie existujúcich iniciatív. Takýto prehľad tiež vysiela signál
                    spoločnosti o dôležitosti vzdelávania ako kľúčovej témy.
                </p>
            </div>

            <ClientOnly>
                <OSMapView
                    class="min-h-[250px] flex-1 rounded-lg md:min-h-[300px] md:max-w-[35%]"
                    :center="mapCenter"
                    height="300px"
                    :marker-icon="actualLocationIcon"
                    :markers="osMapMarkers"
                    :zoom="7"
                    @marker-click="handleMarkerClick"
                />
            </ClientOnly>
        </header>

        <EduvalueFilter
            data-test-id="EduvalueFilters"
            :default-opened="areFiltersActive"
            :forced-filters="forcedFilters"
            :indexes="INDEXES"
            :lang="lang"
            :value="filters"
            @filter-visibility-change="handleFilterVisibilityChange"
            @input="updateFilters"
        >
            <div v-if="searchResults" class="text-gray-500 dark:text-txt-body">
                {{ t('filter.numberOfResults', { count: searchResults.nbHits }) }}
            </div>
        </EduvalueFilter>

        <article v-if="isFilterExpanded && areFiltersActive" class="flex flex-col gap-10 pb-12">
            <PaginatedCardGrid
                v-if="searchResults?.hits.length"
                :disabled="state === 'loading'"
                full-width
                :lang="lang"
                :search-results="searchResults"
                @page="changePage"
            >
                <template #item="{ item }">
                    <OrgCard v-if="item._index === Collections.ORGS" badge-main :org="item" />
                    <SkillCard v-if="item._index === Collections.SKILLS" badge-main :skill="item" />
                    <MentorCard v-if="item._index === Collections.MENTORS" badge-main :mentor="item" />
                    <VolunteerCard v-if="item._index === Collections.VOLUNTEERS" badge-main :volunteer="item" />
                    <JobCard v-if="item._index === Collections.JOBS" badge-main :job="item" />
                    <!-- Pada <ProjectCard v-if="item._index === Collections.PROJECTS" badge-main :project="item" />-->
                </template>
            </PaginatedCardGrid>

            <NothingFound v-else search-term="ale žiadne výsledky vyhovujúce filtru" />
        </article>

        <CLineBreak class="my-5" dark-color />

        <article class="flex flex-col gap-12">
            <section v-if="orgsSearchResults.length">
                <CHeading tag="h2" variant="2">Organizácie</CHeading>

                <MoreItemsCardGrid
                    v-if="sectionsState !== 'loading'"
                    :items="orgsSearchResults"
                    items-gender="she"
                    items-type="orgs"
                    :lang="lang"
                    :show-more-rows="1"
                    :visible-items="ORGS_HITS_PER_PAGE"
                />
            </section>

            <section v-if="opportunitiesSearchResults.length">
                <CHeading tag="h2" variant="2">Príležitosti vo vzdelávaní</CHeading>

                <MoreItemsCardGrid
                    v-if="sectionsState !== 'loading'"
                    :items="opportunitiesSearchResults"
                    items-gender="she"
                    items-type="opportunities"
                    :lang="lang"
                    :show-more-rows="1"
                    :visible-items="OPPORTUNITIES_HITS_PER_PAGE"
                />
            </section>

            <section v-if="usersSearchResults.length">
                <CHeading tag="h2" variant="2">Jednotlivci</CHeading>

                <MoreItemsCardGrid
                    v-if="sectionsState !== 'loading'"
                    :items="usersSearchResults"
                    items-gender="he"
                    items-type="individuals"
                    :lang="lang"
                    :show-more-rows="1"
                    :visible-items="USERS_HITS_PER_PAGE"
                />
            </section>
        </article>
    </div>
</template>

<script setup lang="ts">
import type { HeaderTheme } from '@/components/header/HeaderSticky.vue'
import eduvalueLogo from '@/assets/images/eduvalue/logo.png'
import actualLocationIcon from '@/assets/images/ActualLocation.png'
import debounce from 'lodash-es/debounce'
import { Collections } from '#shared/constants/collections'
import type { PagedSearchResults } from '#shared/types/search'
import { LoadingState } from '#shared/types/LoadingState'
import { MergedOpportunity } from '#shared/types/MergedOpportunities'
import { OpportunityItem } from '@/components/common/MoreItemsCardGrid.vue'
import { useTypesense } from '@/composables/typesense/useTypesense'
import { useExponea } from '@/composables/exponea/useExponea'
import { useMergedOpportunities } from '@/composables/mergedOpportunities/useMergedOpportunities'
import { parseFiltersFromQuery, serializeFiltersIntoQuery } from '@/utils/query'
import { ForcedFiltersInputTypes } from '@/components/filters/EduvalueFilter.vue'

const { joinSearchResults } = useTypesense()
const { trackExponeaEvent } = useExponea()
const { searchMergedOpportunities } = useMergedOpportunities()

type TMarkerPosition = {
    lat: number | undefined
    lng: number | undefined
}

type TMarker = {
    id: string
    position: TMarkerPosition
}

const DEFAULT_MAP_CENTER = {
    lat: 49 as number | undefined,
    lng: 20 as number | undefined
}

const HITS_PER_PAGE = 24
const ORGS_HITS_PER_PAGE = 3
const OPPORTUNITIES_HITS_PER_PAGE = 3
const USERS_HITS_PER_PAGE = 3

const INDEXES = [
    Collections.JOBS,
    Collections.SKILLS,
    Collections.MENTORS,
    Collections.PROJECTS,
    Collections.VOLUNTEERS
]

// Eduvalue collections with filters to find items tagged with "eduvalue"
// Using exact match syntax: field:=`value` for arrays
const EDUVALUE_COLLECTIONS = [
    // Orgs tag eduvalue on `interests` and have no `visibility` field (all orgs are public).
    // Soft-deleted orgs are removed from the Typesense index by the sync, so no deletedAt filter is needed.
    { collection: 'orgs', filterBy: 'interests:=`eduvalue`' },
    { collection: 'jobs', filterBy: 'visibility:=`published` && topics:=`eduvalue`' },
    { collection: 'projects', filterBy: 'visibility:=`published` && topics:=`eduvalue`' },
    { collection: 'skills', filterBy: 'visibility:=`published` && topics:=`eduvalue`' },
    { collection: 'mentors', filterBy: 'visibility:=`published` && interests:=`eduvalue`' },
    { collection: 'volunteers', filterBy: 'visibility:=`published` && interests:=`eduvalue`' }
]

const singleValueKeys = ['q']
const multiValueKeys = ['keywords', 'orgs', 'locations', 'types']

const route = useRoute()
const router = useRouter()
const store = useApplicationStore()
const { t } = useI18n()
// Get the injected functions from the layout
const hideNavbar = inject('hideNavbar') as (value?: boolean) => void
const setNavbarTheme = inject('navbarTheme') as (theme: HeaderTheme) => void

// Data
const lang = ref<'sk' | 'en'>('sk')
const isFilterExpanded = ref(false)
const mapCenter = ref(DEFAULT_MAP_CENTER)
const markers = ref<TMarker[]>([])
const isInitialLoading = ref(true)
const state = ref<LoadingState>('idle')
const sectionsState = ref<LoadingState>('idle')
const searchResults = ref<PagedSearchResults<MergedOpportunity> | undefined>(undefined)
const orgsSearchResults = ref<OpportunityItem[]>([])
const opportunitiesSearchResults = ref<OpportunityItem[]>([])
const usersSearchResults = ref<OpportunityItem[]>([])
const forcedFilters = ref<ForcedFiltersInputTypes>({
    keywords: {},
    orgs: {},
    locations: {},
    types: {}
})

// Computed
const osMapMarkers = computed(() => {
    return markers.value.map((marker) => ({
        position: marker.position as { lat: number; lng: number },
        id: marker.id
    }))
})

const page = computed((): number => {
    const queryPage = route.query.page
    return Number(queryPage) || 1
})

const filters = computed(() => {
    return parseFiltersFromQuery(route.query, {
        singleValueKeys,
        multiValueKeys
    })
})

const filtersQuery = computed(() => {
    return serializeFiltersIntoQuery(filters.value, {
        singleValueKeys,
        multiValueKeys
    })
})

const areFiltersActive = computed(() => {
    return Object.keys(filtersQuery.value).length > 0
})

// Methods
const searchDebounced = debounce(() => searchAll(), 300)

const handleFilterVisibilityChange = (expanded: boolean) => {
    isFilterExpanded.value = expanded
}

const updateFilters = (newFilters: any) => {
    const query = serializeFiltersIntoQuery(newFilters, {
        singleValueKeys,
        multiValueKeys
    })

    router.push({
        name: route.name!,
        query: { ...query, page: undefined }
    })
}

const changePage = (newPage: number) => {
    const query = route.query
    const queryPage = newPage > 1 ? String(newPage) : undefined

    router.push({ query: { ...query, page: queryPage } })

    trackExponeaEvent({
        eventName: 'eduvalue_search',
        userId: store.userId,
        eventProperties: { action: 'change_page', page: newPage }
    })
}

const searchAll = async (requestedPage?: number) => {
    state.value = 'loading'

    const searchPage = requestedPage || page.value || 1
    const hitsPerPage = HITS_PER_PAGE

    // Get keyword, location, and type filters from route query
    const keywordValues = filters.value.keywords || []
    const locationValues = filters.value.locations || []
    const typeValues = filters.value.types || []

    // Build collection-specific filters (different collections have different fields)
    const buildCollectionFilters = (collection: string, baseFilter: string): string => {
        const filterParts: string[] = [baseFilter]

        // Add keyword filter (topics for jobs/projects/skills, interests for orgs/mentors/volunteers)
        // Skip "eduvalue" keyword since it's already in baseFilter
        const extraKeywords = keywordValues.filter((v: string) => v.toLowerCase() !== 'eduvalue')
        if (extraKeywords.length > 0) {
            const keywordsTypesense = extraKeywords.map((v: string) => `\`${v}\``).join(',')
            if (['jobs', 'projects', 'skills'].includes(collection)) {
                filterParts.push(`topics:[${keywordsTypesense}]`)
            } else if (['orgs', 'mentors', 'volunteers'].includes(collection)) {
                filterParts.push(`interests:[${keywordsTypesense}]`)
            }
        }

        // Add location filter
        if (locationValues.length > 0) {
            const locationsTypesense = locationValues.map((v: string) => `\`${v}\``).join(',')
            filterParts.push(`geoCity:[${locationsTypesense}]`)
        }

        return filterParts.filter(Boolean).join(' && ')
    }

    // Filter collections by selected types (if any)
    let filteredCollections = EDUVALUE_COLLECTIONS
    if (typeValues.length > 0) {
        filteredCollections = EDUVALUE_COLLECTIONS.filter(col =>
            typeValues.includes(col.collection)
        )
    }

    // Build collections with their specific filters
    const collectionsWithFilters = filteredCollections.map(col => ({
        ...col,
        filterBy: buildCollectionFilters(col.collection, col.filterBy || '')
    }))

    try {
        // Search with collection-specific filters (eduvalue filter is already in EDUVALUE_COLLECTIONS)
        const results = await searchMergedOpportunities('*', {
            page: searchPage - 1,
            hitsPerPage,
            collections: collectionsWithFilters
        })

        searchResults.value = joinSearchResults(results, searchResults.value)
        state.value = 'idle'

        if (areFiltersActive.value) {
            await handleFilteredMarkers()
        } else {
            await handleNonFilteredMarkers()
        }
    } catch (error) {
        state.value = 'error'
        console.error(error)
    }
}

const transformToOpportunityItems = (
    items: MergedOpportunity[],
    condition: (index: any) => boolean
): OpportunityItem[] => {
    const results: OpportunityItem[] = []

    for (const item of items) {
        if (!condition(item._index)) continue
        results.push({ item, type: item._index })
    }

    return results
}

const searchSections = async () => {
    sectionsState.value = 'loading'

    try {
        // Get all eduvalue-tagged items (filter is already in EDUVALUE_COLLECTIONS)
        const results = await searchMergedOpportunities('*', {
            hitsPerPage: 250,
            collections: EDUVALUE_COLLECTIONS
        })

        // Note: orgs not in EDUVALUE_COLLECTIONS (no interests/topics field in schema)
        orgsSearchResults.value = transformToOpportunityItems(results.hits, (index) => index === Collections.ORGS)

        opportunitiesSearchResults.value = transformToOpportunityItems(
            results.hits,
            (index) =>
                index === Collections.JOBS || index === Collections.PROJECTS || index === Collections.SKILLS
        )

        usersSearchResults.value = transformToOpportunityItems(
            results.hits,
            (index) => index === Collections.MENTORS || index === Collections.VOLUNTEERS
        )

        sectionsState.value = 'idle'
    } catch (error) {
        sectionsState.value = 'error'
        console.error(error)
    }
}

const getLocationsFromEntity = (entity: OpportunityItem['item']): string[] => {
    if ('geocity' in entity && entity.geocity) return [entity.geocity]
    if ('geoCity' in entity && entity.geoCity) return [entity.geoCity]
    if ('placeAddress' in entity && entity.placeAddress) return [entity.placeAddress]
    if ('jobLocation' in entity && entity.jobLocation) return entity.jobLocation
    return []
}

// Cache for geocoding results to avoid repeated lookups (city -> coords | null)
const geocodeCache = new Map<string, { lat: number; lng: number } | null>()

// City strings can carry borough suffixes (e.g. "Bratislava - mestská časť Ružinov").
// Reduce to the base municipality so it matches the server's Slovak-cities lookup.
const normalizeCity = (raw: string): string => raw.split(/\s+-\s+/)[0].trim()

// Batch-geocode city names via the server endpoint. This is same-origin (no CORS), resolves
// most Slovak cities from a local dataset, and falls back to a server-side rate-limited
// Nominatim with a proper User-Agent — avoiding the client-side CORS/429 storm.
const geocodeCities = async (cities: string[]): Promise<void> => {
    if (!process.client) return

    const missing = [...new Set(cities.map(normalizeCity).filter(Boolean))].filter((c) => !geocodeCache.has(c))
    if (!missing.length) return

    try {
        const results = await $fetch<Array<{ city: string; lat: number | null; lng: number | null }>>(
            '/api/cities/geocode',
            { method: 'POST', body: { cities: missing.map((city) => ({ city, country: 'Slovakia' })) } }
        )

        for (const r of results) {
            geocodeCache.set(r.city, r.lat != null && r.lng != null ? { lat: r.lat, lng: r.lng } : null)
        }
    } catch {
        // Leave entries uncached — affected items simply get no pin this pass.
    }
}

// Build one pin per entity from its first resolvable location.
const renderMarkers = async (entities: Array<{ id: string; locations: string[] }>): Promise<void> => {
    if (!process.client) return

    await geocodeCities(entities.flatMap((e) => e.locations))

    const next: TMarker[] = []
    for (const entity of entities) {
        for (const location of entity.locations) {
            const position = geocodeCache.get(normalizeCity(location))
            if (position) {
                next.push({ id: entity.id, position })
                break
            }
        }
    }
    markers.value = next
}

const handleMarkerClick = (marker: any, position: { lat: number; lng: number }) => {
    mapCenter.value = position
}

const handleNonFilteredMarkers = async () => {
    const allEntities = [...orgsSearchResults.value, ...opportunitiesSearchResults.value, ...usersSearchResults.value]

    await renderMarkers(
        allEntities
            .filter((entity) => 'id' in entity.item)
            .map((entity) => ({
                id: `${entity.type}-${(entity.item as any).id}`,
                locations: getLocationsFromEntity(entity.item)
            }))
            .filter((entity) => entity.locations.length)
    )
}

const handleFilteredMarkers = async () => {
    const allEntities = searchResults.value?.hits || []

    await renderMarkers(
        allEntities
            .filter((entity) => 'id' in entity)
            .map((entity) => ({
                id: `${entity._index}-${entity.id}`,
                locations: getLocationsFromEntity(entity)
            }))
            .filter((entity) => entity.locations.length)
    )
}

// Watch for filter and page changes
watch(filters, () => {
    searchDebounced()
})

watch(page, () => {
    searchDebounced()
})

watch(
    areFiltersActive,
    (value) => {
        // Filter starts collapsed, but expand it (panel + results section) when filters are
        // active — e.g. a shared/deep-linked URL. Route-derived, so SSR/client stay consistent.
        if (value && !isFilterExpanded.value) {
            isFilterExpanded.value = true
        }
    },
    { immediate: true }
)

// Lifecycle hooks
onBeforeMount(() => {
    hideNavbar(true)
    setNavbarTheme({
        isStyle: true,
        background: '#6C35EA',
        textColor: 'white',
        buttonVariant: 'eduvalue' as const,
        additionalLogo: eduvalueLogo
    })
})

onMounted(() => {
    if (process.client) {
        isInitialLoading.value = false
    }
})

// onBeforeUnmount
onBeforeUnmount(() => {
    hideNavbar(false)
    setNavbarTheme({
        background: 'bg-white dark:bg-surface-card'
    })
})

// Watch for section results to load markers
watch(
    () => [orgsSearchResults.value, opportunitiesSearchResults.value, usersSearchResults.value],
    () => {
        if (process.client && sectionsState.value === 'idle') {
            handleNonFilteredMarkers()
        }
    },
    { deep: true }
)

// Fetch data
// NOTE: searchAll/searchSections populate refs as a side effect, but useAsyncData only
// serializes its RETURN value into the SSR payload — plain refs set inside the handler are
// not sent to the client, and the handler does not re-run on hydration. So we must return the
// results and re-hydrate the refs from the payload below; otherwise the sections (and the map
// pins derived from them) render on the server and then vanish on the client.
const { data: eduvalueData } = await useAsyncData('eduvalue-data', async () => {
    const userId = store.userId
    const pageNum = Number(route.query.page) || 1
    await Promise.allSettled([searchAll(pageNum), searchSections()])
    return {
        userId,
        searchResults: searchResults.value,
        orgs: orgsSearchResults.value,
        opportunities: opportunitiesSearchResults.value,
        users: usersSearchResults.value
    }
})

// Re-hydrate refs from the serialized payload (assigning these on the client triggers the
// markers watcher, which regenerates the map pins client-side).
if (eduvalueData.value) {
    searchResults.value = eduvalueData.value.searchResults ?? searchResults.value
    orgsSearchResults.value = eduvalueData.value.orgs ?? []
    opportunitiesSearchResults.value = eduvalueData.value.opportunities ?? []
    usersSearchResults.value = eduvalueData.value.users ?? []
}

// Head meta
useHead({
    title: t('eduvalue.title'),
    meta: [{ name: 'description', content: t('eduvalue.description') }]
})
</script>
